import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import './Cuisines.css'
import { AiOutlinePushpin } from "react-icons/ai";
import { getListCover } from "../../../utils/projectImages";
import SortDropdown from "../../../Components/SortDropdown";
import { useSortedItems } from "../../../Hooks/useSortedItems";
import { usePersistedPagination } from "../../../Hooks/usePersistedPagination";

gsap.registerPlugin(ScrollTrigger);

const ITEMS_PER_PAGE = 8;

const cuisinesData = [
  { id: 1, title: "Project 01 — Baie des Anges, Tunisie", link: 'project1' },
  { id: 2, title: "Project 02 — Chaffar, Tunisie",        link: 'project2' },
  { id: 3, title: "Project 03 — Hergla, Tunisie",         link: 'project3' },
  { id: 4, title: "Project 04 — Sfax, Tunisie",           link: 'project4' },
  { id: 5, title: "Project 05 — Tunis, Tunisie",          link: 'project5' },
  { id: 6, title: "Project 06 — Tunis, Tunisie",          link: 'project6' },
  { id: 7, title: "Project 07 — Tunis, Tunisie",          link: 'project7' },
  { id: 8, title: "Project 08 — Tunis, Tunisie",          link: 'project8' },
  { id: 9, title: "Project 09 — Sousse, Tunisie",          link: 'project9' },
    { id: 10, title: "Project 10 — Tunis, Tunisie",          link: 'project10' },
  { id: 11, title: "Project 11 — Megrine, Tunisie",          link: 'project11' },
    { id: 12, title: "Project 12 — Bizerte, Tunisie",          link: 'project12' },
    { id: 13, title: "Project 13 — Tunis, Tunisie",          link: 'project13' },
      { id: 14, title: "Project 14 — Tunis, Tunisie",          link: 'project14' },
    { id: 15, title: "Project 15 — Tunis, Tunisie",          link: 'project15' },
    { id: 16, title: "Project 16 — Hammamet, Tunisie",          link: 'project16' },
    { id: 17, title: "Project 17 — Mornag, Tunisie",          link: 'project17' },
    { id: 18, title: "Project 18 — Tunis, Tunisie",          link: 'project18' },
    { id: 19, title: "Project 19 — Sfax, Tunisie",          link: 'project19' },  
    { id: 20, title: "Project 20 — Tunis, Tunisie",          link: 'project20' },
    { id: 21, title: "Project 21 — Tunis, Tunisie",          link: 'project21' },
].map((item) => ({
  ...item,
  // Image lue dans le dossier du projet : <projet>/<projet>-bottom.jpg
  pic: getListCover("cuisines", item.link),
}));

export default function Cuisines({ navigateWithTransition }) {
  const [currentPage, setCurrentPage, sortOrder, setSortOrder] =
    usePersistedPagination("cuisines");
  const sortedData = useSortedItems(cuisinesData, sortOrder);
  const contentRef = useRef(null);
  const topRef = useRef(null);

  const totalPages = Math.ceil(sortedData.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const visibleItems = sortedData.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Si la page sauvegardée n'existe plus (moins de projets), on revient à la page 1
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    const timeout = setTimeout(() => {
      const ctx = gsap.context(() => {
        const text = container.querySelector(".Cuisine_text");
        const figures = container.querySelectorAll(".Cuisine_figure");

        // ── Texte intro ──────────────────────────────────────────
        gsap.fromTo(
          text,
          { opacity: 0, y: 60 },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: "power2.out",
            delay: currentPage === 1 ? 0.3 : 0,
            scrollTrigger: {
              trigger: text,
              start: "top 88%",
              once: true,
            },
          }
        );

        // ── Figures avec stagger ─────────────────────────────────
        figures.forEach((fig, i) => {
          gsap.fromTo(
            fig,
            { opacity: 0, y: 60 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              ease: "power2.out",
              delay: i * 0.12,
              scrollTrigger: {
                trigger: fig,
                start: "top 88%",
                once: true,
              },
            }
          );
        });
      }, container);

      return () => ctx.revert();
    }, 50);

    return () => clearTimeout(timeout);
  }, [currentPage, sortOrder]);

  const handleSortChange = (order) => {
    setSortOrder(order);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="Cuisines Cuisines--sortable">
      <div ref={topRef} />

      <div ref={contentRef} className="Cuisines_content">
        <div className="Cuisine_text">
          <h2>Cuisines sur mesure</h2>
          <p>
            De la cuisine moderne épurée avec des finitions en bois et quartz à des
            espaces culinaires de style classique alliant élégance et fonctionnalité, nos
            réalisations reflètent la diversité de nos créations. Chaque cuisine est
            adaptée aux goûts et aux besoins de nos clients, tout en utilisant des
            matériaux de qualité supérieure pour garantir longévité et esthétisme.
          </p>
        </div>

        {visibleItems.map((item, index) => (
          <div className="Cuisine_figure" key={item.id}>
            {index === 0 && (
              <div className="Cuisine_sort">
                <SortDropdown value={sortOrder} onChange={handleSortChange} />
              </div>
            )}
            <a href={`/collections/cuisines/${item.link}`}>
              <figure>
                <img src={item.pic} alt={item.title} />
                <figcaption>
                  <AiOutlinePushpin size={16} />
                  <span>{item.title}</span>
                </figcaption>
              </figure>
            </a>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="Cuisines_pagination">
          <button
            className="Pagination_btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Page précédente"
          >
            ‹
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`Pagination_btn ${currentPage === page ? "active" : ""}`}
              onClick={() => handlePageChange(page)}
              aria-label={`Page ${page}`}
            >
              {page}
            </button>
          ))}

          <button
            className="Pagination_btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Page suivante"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}